import get from 'lodash.get';
import { metrics } from './google-analytics';

export const ANALYTICS_CATEGORY = 'performance';

export default function performanceServiceFactory(services) {
  // Holder object to keep track of which URLs we've reported to prevent duplicate reports with cached responses.
  const performanceUrlsReported = {};

  let thisService = {
    resourceTimingTrack,
    viewLoadingStart,
    viewLoadingEnd,
  };

  return thisService;

  ////////////////////

  function resourceTimingTrack(url, friendlyName) {
    if (!get(services.window, 'performance.getEntriesByName')) {
      // Not supported on this browser
      return;
    }

    if (performanceUrlsReported[url]) {
      // Already reported on this URL.
      return;
    }

    if (!friendlyName) {
      friendlyName = url;
    }

    let timings = services.window.performance.getEntriesByName(url, 'resource');
    if (!timings.length) {
      return;
    }

    // Should we take the first one or the "worst" one?
    let timing = timings[0];
    services.ga.eventTrack(ANALYTICS_CATEGORY, 'resource timing', friendlyName, services.window.Math.round(timing.duration), {
      nonInteraction: true,
      [metrics.startDuration]: services.window.Math.round(timing.startTime),
      [metrics.stalled]: services.window.Math.round(timing.requestStart - timing.startTime),
      [metrics.ttfb]: services.window.Math.round(timing.responseStart - timing.requestStart),
      [metrics.download]: services.window.Math.round(timing.responseEnd - timing.responseStart),
      [metrics.endDuration]: services.window.Math.round(timing.responseEnd),
    });

    performanceUrlsReported[url] = true;
  }

  function viewLoadingStart(vm) {
    // We'll assume a full page load here (gazelle will have to account for virtual page views)
    if (!hasUserTimingsApi()) {
      // Not supported on this browser
      return;
    }

    let label = vm.$options._componentTag;
    services.window.performance.mark(`${label}-start`);
  }

  function viewLoadingEnd(vm) {
    if (!hasUserTimingsApi()) {
      // Not supported on this browser
      return;
    }

    // Execute inside of a timeout to get closer to the actual rendering time since
    // this will be queued after DOM updates.
    // See https://stackoverflow.com/a/22541080/277307
    services.window.setTimeout(() => {
      let label = vm.$options._componentTag;
      const startLabel = `${label}-start`;
      const endLabel = `${label}-end`;
      services.window.performance.mark(endLabel);
      services.window.performance.measure(label, startLabel, endLabel);

      let startMark = getPerformanceEntry(startLabel, 'mark');
      if (!startMark) {
        // Not getting all the data we needed - cleanup any remaining data blindly.
        cleanupMarks(startLabel, endLabel, label);
        return;
      }

      let endMark = getPerformanceEntry(endLabel, 'mark');
      if (!endMark) {
        cleanupMarks(startLabel, endLabel, label);
        return;
      }

      let measure = getPerformanceEntry(label, 'measure');
      if (!measure) {
        cleanupMarks(startLabel, endLabel, label);
        return;
      }

      let cachedResources = countCachedResources();

      services.ga.eventTrack(ANALYTICS_CATEGORY, 'view loading', label, services.window.Math.round(measure.duration), {
        nonInteraction: true,
        [metrics.startDuration]: services.window.Math.round(startMark.startTime),
        [metrics.endDuration]: services.window.Math.round(endMark.startTime),
        [metrics.cachedResources]: cachedResources,
      });

      cleanupMarks(startLabel, endLabel, label);
    });
  }

  function cleanupMarks(startLabel, endLabel, measureLabel) {
    services.window.performance.clearMarks(startLabel);
    services.window.performance.clearMarks(endLabel);
    services.window.performance.clearMeasures(measureLabel);
  }

  function countCachedResources() {
    let resourceTimings = services.window.performance.getEntriesByType('resource');
    return resourceTimings.reduce((accumulator, resourceTiming) => {
      // Must check for requestStart because CORS Timing-Allow-Origin header is not on external resources.
      if (resourceTiming.requestStart && !resourceTiming.transferSize) {
        accumulator++;
      }
      return accumulator;
    }, 0);
  }

  function hasUserTimingsApi() {
    return get(services.window, 'performance.mark');
  }

  function getPerformanceEntry(name, type) {
    let entries = services.window.performance.getEntriesByName(name, type);
    if (entries.length !== 1) {
      services.ga.exceptionTrack(`Unexpected number of performance entries for ${name}. Expected 1, found ${entries.length}`);
      return undefined;
    }
    return entries[0];
  }
}
